import { useState } from 'react';
import { T, Var, useGT } from 'gt-react';
import { ChatCircleTextIcon } from '@phosphor-icons/react';
import type { TaskStatus } from '@erp/rpc';
import { Button } from 'src/components/ui/button';
import { Textarea } from 'src/components/ui/textarea';
import { toast } from 'src/components/ui/toast';
import {
  useOpenTaskHelpRequest,
  useProvideTaskInput,
  useTaskAgentThread,
} from 'src/api/rpc/hooks/tasks/useTaskHelpRequest';
import { TaskHelpRequestEvidence } from './TaskHelpRequestEvidence';

interface TaskHelpRequestCardProps {
  /** Minimal task shape — satisfied by TaskResponse and the inbox's Task. */
  task: { id: string; status: TaskStatus };
  /** Optional agent display-name lookup for the "Agent asked" header. */
  agentNameById?: Map<string, string>;
  /**
   * Opens Nami's chat for this task. Supplied only where her `tasks` chat
   * module is mounted (the task detail page); when the open question is
   * Nami's own and the caller has a conversation with her about the task, the
   * card offers to continue there instead of replying in the form.
   */
  onContinueInChat?: () => void;
}

/**
 * The agent's open help request, rendered as a prominent callout with a
 * reply form. Shown only while the task is `needs_input` AND an
 * `agent_help_requested` action exists; submitting calls `tasks.provideInput`,
 * which records the answer and hands the task on — to Nami, who investigates
 * in the background and follows up through the task chat, or back to the
 * agent's queue — so the callout disappears once the reply lands.
 */
export function TaskHelpRequestCard({
  task,
  agentNameById,
  onContinueInChat,
}: TaskHelpRequestCardProps) {
  const isWaitingForInput = task.status === 'needs_input';
  const { data: helpRequest } = useOpenTaskHelpRequest(
    isWaitingForInput ? task.id : null,
  );
  const { data: agentThread } = useTaskAgentThread(
    isWaitingForInput && onContinueInChat ? task.id : null,
  );

  if (!isWaitingForInput || !helpRequest) return null;

  const askedByNami =
    !!helpRequest.agentDefinitionId &&
    !!agentThread?.threadId &&
    helpRequest.agentDefinitionId === agentThread.agentDefinitionId;
  const agentName = helpRequest.agentDefinitionId
    ? agentNameById?.get(helpRequest.agentDefinitionId)
    : undefined;
  const displayAgentName = askedByNami ? 'AI assistant' : agentName;

  return (
    <div
      data-testid="task-help-request-card"
      className="rounded-md border border-[oklch(0.85_0.09_75)] bg-[oklch(0.98_0.03_85)] p-3"
    >
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-[oklch(0.45_0.11_70)]">
        <ChatCircleTextIcon className="size-3.5" weight="fill" />
        {displayAgentName ? (
          <T context="callout heading: a named AI agent asked the user a question">
            <Var>{displayAgentName}</Var> needs your input
          </T>
        ) : (
          <T context="callout heading: an AI agent asked the user a question">
            Agent needs your input
          </T>
        )}
      </div>
      <div className="mt-1.5 whitespace-pre-wrap text-sm font-medium text-stone-900">
        {helpRequest.question}
      </div>
      {helpRequest.context && (
        <div className="mt-1.5 whitespace-pre-wrap text-xs text-stone-700">
          {helpRequest.context}
        </div>
      )}
      {helpRequest.attempted && (
        <div className="mt-1.5 text-xs text-stone-600">
          <span className="font-medium">
            <T context="label preceding what the agent already tried before asking for help">
              Already attempted:
            </T>
          </span>{' '}
          <span className="whitespace-pre-wrap">{helpRequest.attempted}</span>
        </div>
      )}
      <TaskHelpRequestEvidence evidence={helpRequest.evidence} />
      <HelpRequestReplyForm
        taskId={task.id}
        helpRequestActionId={helpRequest.taskActionId}
        onContinueInChat={
          askedByNami && onContinueInChat ? onContinueInChat : undefined
        }
      />
    </div>
  );
}

function HelpRequestReplyForm({
  taskId,
  helpRequestActionId,
  onContinueInChat,
}: {
  taskId: string;
  helpRequestActionId: string;
  onContinueInChat?: () => void;
}) {
  const gt = useGT();
  const [response, setResponse] = useState('');
  const provideInput = useProvideTaskInput(taskId);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!response.trim() || provideInput.isPending) return;
    provideInput.mutate(
      { response: response.trim(), helpRequestActionId },
      {
        onSuccess: (result) => {
          toast.success(
            result.followUp === 'nami'
              ? gt(
                  'Reply sent — the assistant is working on it and will follow up in the task chat.',
                  {
                    context:
                      'toast after a user replies to a help request and Nami picks the task up in the background',
                  },
                )
              : gt('Reply sent — the agent will pick this back up.', {
                  context:
                    'toast after a user replies to an agent help request on a task',
                }),
          );
          setResponse('');
        },
      },
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-2 flex flex-col gap-1.5">
      <Textarea
        value={response}
        onChange={(event) => setResponse(event.target.value)}
        placeholder={gt('Answer the agent\u2019s question…', {
          context: 'placeholder of the reply box for an agent help request',
        })}
        aria-label={gt('Reply to the agent', {
          context: 'reply box for an agent help request',
        })}
        rows={3}
        disabled={provideInput.isPending}
        className="bg-white text-sm"
      />
      {provideInput.isError && (
        <div className="text-xs text-destructive" role="alert">
          <T context="error shown when sending a reply to an agent failed">
            Could not send your reply. Please try again.
          </T>
        </div>
      )}
      <div className="flex items-center justify-end gap-2">
        <span className="text-[11px] text-muted-foreground">
          {onContinueInChat ? (
            <T context="hint under the reply box when Nami asked the question: the user can reply here or in her chat">
              Reply here or continue the conversation with the assistant.
            </T>
          ) : (
            <T context="hint under the reply box: replying hands the task on to an AI agent">
              Your reply is recorded on the task and an agent picks it up.
            </T>
          )}
        </span>
        {onContinueInChat && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onContinueInChat}
            disabled={provideInput.isPending}
          >
            <ChatCircleTextIcon className="size-3.5" />
            {gt('Continue in chat', {
              context:
                'button opening the Nami chat panel to answer her question about a task',
            })}
          </Button>
        )}
        <Button
          type="submit"
          size="sm"
          disabled={!response.trim() || provideInput.isPending}
        >
          {provideInput.isPending
            ? gt('Sending…', { context: 'reply submit button, in flight' })
            : gt('Send reply', {
                context: 'button submitting a reply to an agent help request',
              })}
        </Button>
      </div>
    </form>
  );
}
